import { ReactNode, createContext, useMemo, useReducer } from 'react';
import { TIMELINE_END_MINUTES, TIMELINE_START_MINUTES } from './constants';
import { getMinCreatableMinutes } from './helpers/time';
import { useFocusTimeEffects } from './hooks/useFocusTimeEffects';
import { useFocusTimePersistence } from './hooks/useFocusTimePersistence';
import { focusTimeReducer, initialFocusTimeState } from './reducer';
import { FocusTimeContextType } from './types';

export const FocusTimeContext = createContext<FocusTimeContextType | undefined>(undefined);

type FocusTimeProviderProps = Readonly<{
  children: ReactNode;
}>;

const FocusTimeProvider = ({ children }: FocusTimeProviderProps) => {
  const [{ slots, currentMinutes }, dispatch] = useReducer(focusTimeReducer, initialFocusTimeState);

  const { setSlots, saveSlots } = useFocusTimePersistence({
    dispatch,
  });

  useFocusTimeEffects({
    slots,
    dispatch,
    setSlots,
  });

  const visibleCurrentMinutes =
    currentMinutes >= TIMELINE_START_MINUTES && currentMinutes <= TIMELINE_END_MINUTES ? currentMinutes : null;

  const value = useMemo(
    () => ({
      slots,
      currentMinutes: visibleCurrentMinutes,
      minSelectableMinutes: getMinCreatableMinutes(),
      saveSlots,
    }),
    [saveSlots, slots, visibleCurrentMinutes],
  );

  return <FocusTimeContext.Provider value={value}>{children}</FocusTimeContext.Provider>;
};

export default FocusTimeProvider;
